import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import treatmentRecordService from '../services/TreatmentRecordService';
import customerService from '../services/CustomerService';
import practitionerService from '../services/PractitionerService';
import treatmentService from '../services/TreatmentService';
import appointmentService from '../services/AppointmentService';
import WorkOrderService from '../services/WorkOrderService';
import TreatmentRecordForm from '../components/treatments/TreatmentRecordForm';
import { Card, Button, Alert } from '../components/ui';
import TreatmentRecordTabsEditor from '../components/treatments/TreatmentRecordTabsEditor';
import CustomerInfo from '../components/customers/CustomerInfo';
import { workOrderStatusLabels } from '../constants/statusLabels';

const TABS = [
  { key: 'health', label: '健康档案与评估' },
  { key: 'tcm', label: '四诊信息' },
  { key: 'plan', label: '调理方案' },
  { key: 'service', label: '服务实施' },
  { key: 'follow', label: '随访与结算' },
  { key: 'attachments', label: '附件' },
];

/**
 * TreatmentRecordDetailPage component for viewing and managing a specific treatment record
 * @returns {React.ReactElement} TreatmentRecordDetailPage component
 */
const TreatmentRecordDetailPage = () => {
  // Get record id from URL params
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // State
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reference data for display
  const [customer, setCustomer] = useState(null);
  const [treatment, setTreatment] = useState(null);
  const [practitioner, setPractitioner] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState(TABS[0].key);

  // Check permissions
  const canViewRecord = hasPermission('treatmentRecords', 'read');
  const canEditRecord = hasPermission('treatmentRecords', 'write');

  // Fetch record data
  useEffect(() => {
    if (!id || !canViewRecord) {
      return;
    }

    const fetchRecordData = async () => {
      setLoading(true);
      try {
        // Fetch the record
        const recordData = treatmentRecordService.getTreatmentRecordById(id);
        if (!recordData) {
          setError('找不到调理记录');
          return;
        }
        setRecord(recordData);

        // Fetch related entities
        const customersData = customerService.getAllCustomers();
        setCustomers(customersData);

        const customerData = customerService.getCustomerById(recordData.customerId);
        const treatmentData = treatmentService.getTreatmentById(recordData.treatmentId);
        const practitionerData = practitionerService.getPractitionerById(recordData.practitionerId);

        setCustomer(customerData);
        setTreatment(treatmentData);
        setPractitioner(practitionerData);

        // If there's an associated appointment, fetch it
        if (recordData.appointmentId) {
          const appointmentData = appointmentService.getAppointmentById(recordData.appointmentId);
          setAppointment(appointmentData);
        }

        if (recordData.workOrderId) {
          setWorkOrder(WorkOrderService.getWorkOrderById(recordData.workOrderId));
        }
      } catch (err) {
        console.error('Error fetching record data:', err);
        setError('获取调理记录失败');
      } finally {
        setLoading(false);
      }
    };

    fetchRecordData();
  }, [id, canViewRecord]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '未记录';
    const date = new Date(dateString);

    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle delete record
  const handleDelete = () => {
    if (window.confirm('确定要删除此调理记录吗？此操作无法撤销。')) {
      try {
        treatmentRecordService.deleteTreatmentRecord(id);
        navigate('/treatment-records');
      } catch (err) {
        console.error('Error deleting record:', err);
        alert('删除调理记录失败');
      }
    }
  };

  // Handle form submission
  const handleSubmit = (formData) => {
    setIsSubmitting(true);

    try {
      treatmentRecordService.updateTreatmentRecord(id, formData);

      // Refresh record data
      const updatedRecord = treatmentRecordService.getTreatmentRecordById(id);
      setRecord(updatedRecord);

      // Refresh related entities
      const treatmentData = treatmentService.getTreatmentById(updatedRecord.treatmentId);
      const practitionerData = practitionerService.getPractitionerById(updatedRecord.practitionerId);

      setTreatment(treatmentData);
      setPractitioner(practitionerData);

      // If there's an associated appointment, update it
      if (updatedRecord.appointmentId) {
        const appointmentData = appointmentService.getAppointmentById(updatedRecord.appointmentId);
        setAppointment(appointmentData);
      } else {
        setAppointment(null);
      }

      setShowEditForm(false);
    } catch (err) {
      console.error('Error updating record:', err);
      alert('更新调理记录失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 头部按钮区（只保留返回按钮和主标题，无编辑/删除按钮）
  const renderHeader = () => (
    <Card variant="glass" className="border-emerald-100 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/treatment-records')}
            className="shrink-0"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            返回调理记录
          </Button>
          <div className="border-l border-gray-200 pl-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {loading ? '加载调理记录...' : '调理记录详情'}
            </h1>
            <p className="text-gray-600">调理记录详细信息及流程</p>
          </div>
        </div>
      </div>
    </Card>
  );

  // 错误、无权限、加载等状态友好提示
  if (error === '找不到调理记录') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            调理记录未找到
          </h2>
          <p className="text-gray-600 mb-6">
            您尝试访问的调理记录不存在或已被删除
          </p>
          <Button as={Link} to="/treatment-records">
            返回调理记录列表
          </Button>
        </Card>
      </div>
    );
  }

  if (!canViewRecord) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            权限不足
          </h2>
          <p className="text-gray-600 mb-6">
            您没有查看调理记录详情的权限
          </p>
          <Button as={Link} to="/">
            返回首页
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {renderHeader()}
        {error && error !== '找不到调理记录' && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Card variant="modern">
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent"></div>
            </div>
          </Card>
        ) : record ? (
          <>
            {/* 顶部基本信息紧凑行（优化字体和分布） */}
            <div className="flex flex-nowrap items-center justify-between gap-x-4 p-2 text-sm font-medium overflow-x-auto mb-2">
              <div className="whitespace-nowrap">
                <span className="text-gray-400">工单ID：</span>
                <span className="font-mono text-gray-900">{workOrder?.id || '无'}</span>
              </div>
              <div className="flex-1 min-w-0 truncate">
                <span className="text-gray-400">状态：</span>
                <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{workOrderStatusLabels[workOrder?.status] || '无'}</span>
              </div>
              <div className="flex-1 min-w-0 truncate">
                <span className="text-gray-400">调理项目：</span>
                <span className="text-gray-900">{record?.treatmentProjectName || '未指定项目'}</span>
              </div>
              <div className="flex-1 min-w-0 truncate">
                <span className="text-gray-400">调理日期：</span>
                <span className="text-gray-900">{formatDate(record?.date)}</span>
              </div>
              <div className="flex-1 min-w-0 truncate">
                <span className="text-gray-400">调理师：</span>
                <span className="text-gray-900">{practitioner ? practitioner.name : '未指定调理师'}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 左侧客户信息卡片 */}
              <div className="lg:col-span-1">
                <CustomerInfo customer={customer} onEdit={() => {}} loading={loading} editable={false} />
              </div>
              {/* 右侧Tab内容卡片（编辑模式） */}
              <div className="lg:col-span-2 space-y-6">
                <TreatmentRecordTabsEditor
                  record={record}
                  onSave={(updated, updatedWorkOrder) => {
                    setRecord(updated);
                    if (updatedWorkOrder) setWorkOrder(updatedWorkOrder);
                  }}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default TreatmentRecordDetailPage;